<template>
  <h3>{{ $t("user.registerTitle") }}</h3>

  <SocialLoginButtons @loginSuccess="onSocialLoginSuccess" />

  <form class="form" @submit.prevent="onSubmit()">
    <FormGroup :v="v$.name" label="label.fullName">
      <TextField
        v-model="formValue.name"
        :disabled="props.saving"
        placeholder="placeholder.fullName"
        autocomplete="name"
      />
    </FormGroup>
    <FormGroup :v="v$.email" label="label.email">
      <TextField
        v-model="formValue.email"
        type="text"
        inputmode="email"
        :disabled="props.saving"
        placeholder="placeholder.email"
        autocomplete="username"
        autocapitalize="off"
      />
    </FormGroup>
    <FormGroup :v="v$.phone" label="label.phone">
      <TextField
        v-model="formValue.phone"
        :disabled="props.saving"
        type="tel"
        placeholder="placeholder.phone"
        autocomplete="tel"
      />
    </FormGroup>
    <FormGroup :v="v$.password" label="label.password">
      <PasswordField
        v-model="formValue.password"
        :disabled="props.saving"
        autocapitalize="off"
        autocomplete="new-password"
      />
    </FormGroup>
    <FormGroup :v="v$.password_confirmation" label="label.repeatPassword">
      <PasswordField
        v-model="formValue.password_confirmation"
        :disabled="props.saving"
        autocomplete="new-password"
        autocapitalize="off"
      />
    </FormGroup>
    <FormGroup :v="v$.reservation_reminder_email" :label="null">
      <ToggleField
        id="register-reservation-reminder-email"
        v-model="formValue.reservation_reminder_email"
        :disabled="props.saving"
      >
        {{ $t("profile.notifications.reservationReminderEmail") }}
      </ToggleField>
    </FormGroup>
    <FormGroup :v="v$.reservation_reminder_sms" :label="null">
      <ToggleField
        id="register-reservation-reminder-sms"
        v-model="formValue.reservation_reminder_sms"
        :disabled="props.saving"
      >
        {{ $t("profile.notifications.reservationReminderSms") }}
      </ToggleField>
    </FormGroup>
    <FormGroup :v="v$.newsletter" :label="null">
      <ToggleField
        id="register-newsletter"
        v-model="formValue.newsletter"
        :disabled="props.saving"
      >
        {{ $t("profile.notifications.newsletter") }}
      </ToggleField>
    </FormGroup>

    <FormGroup :v="v$.agree" label="">
      <CheckboxField v-model="formValue.agree" :disabled="props.saving">
        <i18n-t keypath="user.agreePolicy" tag="span">
          <template #link>
            <a href="/privacy" target="_blank" rel="noopener noreferrer">{{
              $t("user.agreePolicyLink")
            }}</a>
          </template>
        </i18n-t>
      </CheckboxField>
    </FormGroup>

    <div class="form__actions">
      <AppButton type="submit" :loading="props.saving">
        {{ $t("user.register") }}
      </AppButton>
    </div>
  </form>
</template>

<script setup lang="ts">
import FormGroup from "@/components/form/form-group.vue";
import TextField from "@/components/form/text-field.vue";
import ToggleField from "@/components/form/toggle-field.vue";
import AppButton from "@/components/app-button.vue";
import PasswordField from "@/components/form/password-field.vue";
import SocialLoginButtons from "./social-login/social-login-buttons.vue";
import { computed, reactive } from "vue";
import { UserRegisterData } from "@/types";
import {
  email,
  maxLength,
  minLength,
  required,
  requiredIf,
  sameAs,
} from "@vuelidate/validators";
import { equal, fullName, phone } from "@/utils/validators";
import useVuelidate from "@vuelidate/core";
import CheckboxField from "@/components/form/checkbox-field.vue";

const props = defineProps<{
  saving?: boolean;
  errors?: Record<string, string[]>;
}>();

const emit = defineEmits<{
  (e: "submit", v: UserRegisterData): void;
  (e: "socialLoginSuccess", payload: { action: "login" | "register" }): void;
}>();

const formValue = reactive<
  UserRegisterData & { password_confirmation: string; agree: boolean }
>({
  name: "",
  email: "",
  phone: "",
  password: "",
  password_confirmation: "",
  agree: false,
  reservation_reminder_email: false,
  reservation_reminder_sms: false,
  newsletter: false,
});

const rules = computed(() => ({
  name: { required, maxLength: maxLength(255), fullName },
  email: {
    required,
    email,
    maxLength: maxLength(150),
  },
  phone: {
    maxLength: maxLength(32),
    phone,
    required: requiredIf(formValue.reservation_reminder_sms),
  },
  password: { required, minLength: minLength(8) },
  password_confirmation: {
    required,
    equalPassword: sameAs(formValue.password),
  },
  agree: { agree: equal(true) },
  reservation_reminder_email: {},
  reservation_reminder_sms: {},
  newsletter: {},
}));

const v$ = useVuelidate(rules, formValue, {
  $externalResults: computed(() => props.errors) as any,
});

const onSubmit = () => {
  v$.value.$touch();

  if (!v$.value.$invalid) {
    emit("submit", {
      name: formValue.name,
      email: formValue.email,
      phone: formValue.phone,
      password: formValue.password,
      password_confirmation: formValue.password_confirmation,
      reservation_reminder_email: formValue.reservation_reminder_email,
      reservation_reminder_sms: formValue.reservation_reminder_sms,
      newsletter: formValue.newsletter,
    });
  }
};

const onSocialLoginSuccess = (payload: { action: "login" | "register" }) => {
  emit("socialLoginSuccess", payload);
};
</script>
